import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useQueryClient } from "@tanstack/react-query";
import {
  isValidDollarAmount,
  dollarsToCents,
  formatCentsAsDollars,
} from "@/lib/price-utils";

export interface BaseItem {
  uid: string;
  title: string;
  cost: number;
  description?: string | null;
  createdAt: string | Date;
}

interface BaseItemDialogProps<T extends BaseItem> {
  isOpen: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  item?: T;
  orgId: string;
  onItemAdded?: (itemId: string, item?: T) => void;
  onItemUpdated?: (item: T) => void;
  apiEndpoint: string;
  queryKey: string;
  itemType: string;
  itemTypeCapitalized: string;
  showDescription?: boolean;
  descriptionLabel?: string;
  descriptionPlaceholder?: string;
}

export function BaseItemDialog<T extends BaseItem>({
  isOpen,
  onClose,
  mode,
  item,
  orgId: _orgId,
  onItemAdded,
  onItemUpdated,
  apiEndpoint,
  queryKey,
  itemType,
  itemTypeCapitalized,
  showDescription = false,
  descriptionLabel = i18n.t("components.shared.baseItemDialog.descriptionOptional"),
  descriptionPlaceholder = i18n.t("components.shared.baseItemDialog.detailedDescription"),
}: BaseItemDialogProps<T>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // States for form fields
  const [itemTitle, setItemTitle] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemCost, setItemCost] = useState("");

  // States for validation errors
  const [titleError, setTitleError] = useState("");
  const [costError, setCostError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form values when item changes or dialog mode changes
  useEffect(() => {
    if (mode === "edit" && item) {
      setItemTitle(item.title);
      setItemDescription(item.description || "");
      // Convert cents to dollars for display
      setItemCost(formatCentsAsDollars(item.cost));
    } else {
      setItemTitle("");
      setItemDescription("");
      setItemCost("");
    }
    // Clear errors when dialog opens/closes or mode changes
    setTitleError("");
    setCostError("");
    setIsSubmitting(false);
  }, [mode, item, isOpen]);

  const handleSave = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Clear previous errors
    setTitleError("");
    setCostError("");

    let hasErrors = false;

    // Lowercased translated noun (e.g. "service" / "servicio") for prose.
    const itemTypeLowercase = itemTypeCapitalized.toLowerCase();

    // Validate input
    if (!itemTitle.trim()) {
      setTitleError(
        t("components.shared.baseItemDialog.titleRequired", {
          type: itemTypeLowercase,
        }),
      );
      hasErrors = true;
    }

    if (!itemCost.trim()) {
      setCostError(t("components.shared.baseItemDialog.costRequired"));
      hasErrors = true;
      return;
    }

    if (!isValidDollarAmount(itemCost)) {
      setCostError(t("components.shared.baseItemDialog.invalidDollarAmount"));
      hasErrors = true;
      return;
    }

    const dollarAmount = parseFloat(itemCost);
    if (isNaN(dollarAmount) || dollarAmount < 0) {
      setCostError(t("components.shared.baseItemDialog.costMustBePositive"));
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert dollar input to cents for storage
      const costInCents = dollarsToCents(dollarAmount);

      const itemData = {
        title: itemTitle.trim(),
        cost: costInCents,
        ...(showDescription && { description: itemDescription.trim() }),
      };

      if (mode === "add") {
        // Create new item
        const res = await apiRequest("POST", apiEndpoint, itemData);

        if (!res.ok) {
          const errorText = await res.text();
          console.error(
            `${itemTypeCapitalized} creation error response:`,
            errorText,
          );
          throw new Error(
            `Failed to create ${itemType}: ${res.status} ${res.statusText}`,
          );
        }

        const newItem = await res.json();

        // Refresh data
        queryClient.invalidateQueries({ queryKey: [queryKey] });

        toast({
          title: t("components.shared.baseItemDialog.addedSuccessfully", {
            type: itemTypeCapitalized,
          }),
        });

        // Notify parent component
        if (onItemAdded && newItem && newItem.uid) {
          onItemAdded(newItem.uid, newItem);
        }
      } else if (mode === "edit" && item) {
        // Update existing item
        const res = await apiRequest(
          "PATCH",
          `${apiEndpoint}/${item.uid}`,
          itemData,
        );

        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Failed to update ${itemType}: ${errorText}`);
        }

        const updatedItem = await res.json();

        // Refresh data
        queryClient.invalidateQueries({ queryKey: [queryKey] });

        toast({
          title: t("components.shared.baseItemDialog.updatedSuccessfully", {
            type: itemTypeCapitalized,
          }),
        });

        // Notify parent component
        if (onItemUpdated) {
          onItemUpdated(updatedItem);
        }
      }

      // Close dialog
      onClose();
    } catch (error: any) {
      console.error(`Error saving ${itemType}:`, error);
      toast({
        title: t(
          mode === "add"
            ? "components.shared.baseItemDialog.failedToAdd"
            : "components.shared.baseItemDialog.failedToUpdate",
          { type: itemTypeCapitalized.toLowerCase() },
        ),
        description:
          error.message || t("components.shared.baseItemDialog.pleaseTryAgain"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {t(
              mode === "add"
                ? "components.shared.baseItemDialog.addNew"
                : "components.shared.baseItemDialog.editType",
              { type: itemTypeCapitalized },
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              mode === "add"
                ? "components.shared.baseItemDialog.createDescription"
                : "components.shared.baseItemDialog.updateDescription",
              { type: itemTypeCapitalized.toLowerCase() },
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSave} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="item-title">
              {t("components.shared.baseItemDialog.titleLabel", {
                type: itemTypeCapitalized,
              })}
            </Label>
            <Input
              id="item-title"
              value={itemTitle}
              onChange={(e) => {
                setItemTitle(e.target.value);
                // Clear error when user starts typing
                if (titleError) setTitleError("");
              }}
              placeholder={t("components.shared.baseItemDialog.namePlaceholder", {
                type: itemTypeCapitalized,
              })}
              className={titleError ? "border-red-500" : ""}
            />
            {titleError && <p className="text-sm text-red-500">{titleError}</p>}
          </div>

          {showDescription && (
            <div className="space-y-2">
              <Label htmlFor="item-description">{descriptionLabel}</Label>
              <Textarea
                id="item-description"
                value={itemDescription}
                onChange={(e) => setItemDescription(e.target.value)}
                placeholder={descriptionPlaceholder}
                rows={3}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="item-cost">{t("components.shared.baseItemDialog.cost")}</Label>
            <Input
              id="item-cost"
              value={itemCost}
              onChange={(e) => {
                const value = e.target.value;
                // Only allow valid dollar amounts
                if (isValidDollarAmount(value)) {
                  const numericValue = value === "" ? 0 : parseFloat(value);
                  // Check maximum value (999999.99)
                  if (numericValue <= 999999.99) {
                    setItemCost(value);
                    // Clear error when user starts typing valid input
                    if (costError) setCostError("");
                  }
                }
              }}
              type="text"
              placeholder="0.00"
              className={costError ? "border-red-500" : ""}
              max="999999.99"
            />
            {costError && <p className="text-sm text-red-500">{costError}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t("common.saving")
                : t(
                    mode === "add"
                      ? "components.shared.baseItemDialog.addButton"
                      : "components.shared.baseItemDialog.updateButton",
                    { type: itemTypeCapitalized },
                  )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
